import { FC } from 'react';
import { StatusBadge } from '@/components/ui/status-badge';
import { SkillState } from '@/types/device';
import { cx } from '@/utils/cx';

type Props = {
  skills: SkillState[] | undefined;
};

const formatStage = (stage: string | undefined): string => stage ?? '—';

const SkillRow: FC<{ skill: SkillState }> = ({ skill }) => {
  const {
    skillName,
    skillId,
    declaredEnabled,
    declaredStage,
    actualEnabled,
    actualStage,
    divergent,
  } = skill;

  return (
    <div className="flex flex-row items-start justify-between gap-4 border-b border-gray-200 py-3 text-sm font-medium">
      <div className="flex flex-col">
        <span>{skillName ?? skillId}</span>
        {skillName && (
          <span className="text-xs font-normal text-gray-500">{skillId}</span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <div
          className={cx(
            'flex items-center gap-1 rounded-sm px-2 py-1',
            divergent && 'bg-warningTint',
          )}
        >
          <StatusBadge
            text={actualEnabled ? 'Enabled' : 'Disabled'}
            variant={
              divergent ? 'warning' : actualEnabled ? 'success' : 'alert'
            }
          />
          <span
            className={cx(
              'text-xs',
              divergent ? 'text-warningShade font-medium' : 'text-gray-500',
            )}
          >
            {formatStage(actualStage)}
          </span>
          {divergent && (
            <span className="text-warningShade text-xs">
              (expected:{' '}
              {declaredEnabled
                ? `enabled / ${formatStage(declaredStage)}`
                : 'disabled'}
              )
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const DeviceSkillsSection: FC<Props> = ({ skills }) => {
  if (!skills || skills.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 flex flex-col">
      <div className="border-b border-gray-200 pt-1.5 pb-2 text-base font-medium">
        Skills
      </div>
      {skills.map((skill) => (
        <SkillRow key={skill.skillId} skill={skill} />
      ))}
    </div>
  );
};
